import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { hasActiveProductsToBeDisplayed } from "@qgiv/p2p-registration";
import { constants } from "@qgiv/core-js";
import { ThemeType } from "../../../../../redux/constants";
import { selectHasParentRegistration } from "../../../../../redux/slices/appSettingsSlice";
import { selectIsCms } from "../../../../../redux/slices/configSlice";
import {
    selectAllP2pSettings,
    selectBaseUrl,
} from "../../../../../redux/slices/p2pSettingsSlice";
import { selectAllClassifications } from "../../../../../redux/slices/classificationsSlice";
import { selectAllProducts } from "../../../../../redux/slices/productsSlice";
import { selectAllRegistration } from "../../../../../redux/slices/registrationSlice";
import { selectThemeSettings } from "../../../../../redux/slices/cmsSettingsSlice";
import RegistrationNav from "./RegistrationNav";

const ConnectedRegistrationNav = ({ isMediumScreen }) => {
    const {
        ENUMS: {
            EntityType: { REGISTRATION, TEAM },
        },
    } = constants;
    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const isCms = useSelector(selectIsCms);

    const classifications = useSelector(selectAllClassifications);
    const products = useSelector(selectAllProducts);
    const {
        classification = {},
        registrations = [],
        storeCart = [],
    } = useSelector(selectAllRegistration);

    const p2pSettings = useSelector(selectAllP2pSettings);
    const { classificationLabel = "Classifications", enableClassifications } =
        p2pSettings;
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.HEADER, state),
    );

    const baseUrl = useSelector(selectBaseUrl);

    const {
        primaryRegProgressBar: {
            checkoutStepLabel = "Finish Up",
            classificationStepLabel = "%ClassificationLabel%",
            participantDetailsStepLabel = "Details",
            storeStepLabel = "Add-ons",
        },
    } = themeSettings;

    // -------------------------------------------------------------------------
    // TODO: Once classification check selector is created, replace logic below with value
    //       of that selector. Will also want to create registration selector of similar type
    // -------------------------------------------------------------------------

    const displayClassificationNav =
        !hasParentRegistration &&
        enableClassifications &&
        classifications &&
        classifications.length > 0;

    // -------------------------------------------------------------------------
    // NOTE: If classification.Classification is undefined, meaning a classification
    //       has not been selected yet, this check will return undefined. By using
    //       this ternary, we can force it to be false
    // -------------------------------------------------------------------------
    const classificationStepComplete = !!(
        classification.Classification &&
        classification.Classification.length > 0
    );

    const getCurrentRegistrationRoleHasBeenCompleted = (values) => {
        const currentRoleHasBeenSelected =
            values?.Registration_Role?.length > 0;
        const isIndividualOrHasSelectedTeam =
            values?.Registration_Role === REGISTRATION ||
            (values?.Registration_Role === TEAM && values?.Team?.length > 0);
        const currentRegistrationRoleHasBeenCompleted =
            currentRoleHasBeenSelected && isIndividualOrHasSelectedTeam;

        return currentRegistrationRoleHasBeenCompleted;
    };

    const registrationStepComplete = Object.keys(registrations[0]).length > 0;

    const displayStoreNav = hasActiveProductsToBeDisplayed(products);

    const storeStepComplete = storeCart && storeCart.length > 0;

    const routes = [
        {
            name:
                classificationStepLabel === ""
                    ? "Classifications"
                    : classificationStepLabel.replace(
                          "%ClassificationLabel%",
                          classificationLabel === ""
                              ? "Classifications"
                              : classificationLabel,
                      ),
            path: `${baseUrl}/form/classification`,
            displayRoute: displayClassificationNav,
            stepComplete: classificationStepComplete,
        },
        {
            name: participantDetailsStepLabel,
            path: `${baseUrl}/form/registration`,
            displayRoute: true,
            stepComplete: registrationStepComplete,
        },
        {
            name: storeStepLabel,
            path: `${baseUrl}/form/store`,
            displayRoute: displayStoreNav,
            stepComplete: storeStepComplete,
        },
        {
            name: checkoutStepLabel,
            path: `${baseUrl}/form/payment`,
            displayRoute: true,
            stepComplete: false,
        },
    ];
    const registrationNavProps = {
        baseUrl,
        displayClassificationNav,
        displayStoreNav,
        getCurrentRegistrationRoleHasBeenCompleted,
        isCms,
        routes,
        isMediumScreen,
    };

    return <RegistrationNav {...registrationNavProps} />;
};

ConnectedRegistrationNav.propTypes = {
    isMediumScreen: PropTypes.bool.isRequired,
};

export default ConnectedRegistrationNav;
